import { format } from 'date-fns';
import isString from 'lodash.isstring';
import {
  SubscriptionData,
  SubscriptionEntitlementType,
  PaypalAccount,
  VenmoAccount,
} from 'src/utilities/types';
import { get } from 'src/shared/dependencies/nodash/get';
import { getFromQuery } from 'src/shared/utilities/get-from-query.utility';
import { Option } from 'src/components/SubscriptionSelect/SubscriptionSelect';

import { SessionStorageUtility } from 'src/shared/utilities/sessionStorage.utility';
import { uuidToBase64 } from 'src/shared/utilities/uuidToBase64';
import { CreditCard } from 'src/shared/models/credit-card-model';

export const RETURN_URL_STORAGE_KEY = 'hybrid-webredirectUrl';
export const MANAGED_SUBSCRIPTION_PARTNERS = {
  LYFT: 'LYFT',
  AMAZON_PRIME: 'AMAZON_PRIME',
};

export interface SubscriptionResponse {
  subscription: SubscriptionData;
  migrationTargetSubscription?: SubscriptionData;
  availableEntitlementTypes: SubscriptionEntitlementType[];
  inAuthTransactionId?: string; // Only populated for /new subscriptions
}

export type CheckEligibilityOptions = {
  giveawayId: string;
  token?: string;
};

export function getFormattedSubscriptionData(subscription: SubscriptionData) {
  const purchasePage = get(subscription.texts, 'purchase_page');

  const planInfo: {
    cost: string;
    title: string;
  } = get(purchasePage, 'plan_info');
  const bannerText: string = get(purchasePage, 'banner_text');
  const bullets: {
    1: { text: string; image_url: string };
    2: { text: string; image_url: string };
    3: { text: string; image_url: string };
    4: { text: string; image_url: string };
  } = get(purchasePage, 'bullets');

  return {
    planInfo,
    bannerText,
    bullets,
  };
}

export function getIsSubscriptionCanceled(
  subscription: SubscriptionData,
): boolean {
  if (!subscription) {
    return false;
  }
  return subscription.status === 'CANCELLED';
}

export function getIsSubscriptionPending(
  subscription: SubscriptionData,
): boolean {
  return (
    subscription?.status === 'CANCELLED' &&
    subscription?.managed_settings?.status === 'PENDING'
  );
}

/**
 * Return true if the subscription's is active as determined by it's status
 */
export function getIsSubscriptionPurchasable(
  subscription: SubscriptionData,
): boolean {
  if (!subscription) {
    return false;
  }
  return subscription.status === 'NEW';
}

/**
 * Return true if the subscription's is active as determined by it's status
 */
export function getIsSubscriptionActive(
  subscription: SubscriptionData,
): boolean {
  if (!subscription) {
    return false;
  }
  return subscription.status === 'EXISTING';
}

export function getIsSubscriptionPaused(
  subscription: SubscriptionData,
): boolean {
  return (
    subscription.status === 'EXPIRED' &&
    subscription?.managed_settings?.status === 'PAUSED'
  );
}

export function getHighestPriorityActiveSubscription(
  subscriptions: SubscriptionData[] | undefined | null,
): SubscriptionData | null {
  if (!subscriptions?.length) {
    return null;
  }

  const eligible = subscriptions.filter(
    (subscription) =>
      subscription.status === 'EXISTING' || subscription.status === 'CANCELLED',
  );

  if (!eligible.length) {
    return null;
  }

  const subscriptionStatusPriority: Record<'EXISTING' | 'CANCELLED', number> = {
    EXISTING: 0,
    CANCELLED: 1,
  };

  return [...eligible].reduce(
    (highest, current) => {
      if (!highest) {
        return current;
      }
      return subscriptionStatusPriority[current.status] <
        subscriptionStatusPriority[highest.status]
        ? current
        : highest;
    },
    null as SubscriptionData | null,
  );
}

export function getIsSubscriptionInMigration(
  subscription: SubscriptionData,
): boolean {
  return !!subscription?.plan_migration_association?.target_subscription_id;
}

/**
 * Returns if the user can cancel their own subscription
 *
 */
export function canCancelSubscription(subscription: SubscriptionData): boolean {
  if (!subscription) {
    return false;
  }

  const managedSettings = get(subscription, 'managed_settings');

  return !managedSettings || !!get(managedSettings, 'cancellable');
}

/**
 * Split the list on new lines, ensuring we remove any trailing new lines
 */
export function parseResponse(text: string): string[] {
  if (!text) {
    return [];
  }

  return text.split('\n').filter((option) => {
    return option !== '';
  });
}

export function setEndOfFlowDestination() {
  const redirectUrl = getFromQuery('purchaseRedirectUrl');
  if (redirectUrl) {
    const storage = new SessionStorageUtility();
    storage.set(RETURN_URL_STORAGE_KEY, redirectUrl);
  }
}

export function fetchEndOfFlowDestination() {
  const storage = new SessionStorageUtility();
  return storage.get(RETURN_URL_STORAGE_KEY) || '';
}
/**
 * @param subscription The subscription to check
 * @returns whether the subscription is a trial
 */
export function isTrialSubscription(subscription: SubscriptionData): boolean {
  if (!subscription) {
    return false;
  }
  const { diner_association: dinerAssociation } = subscription;
  if (!dinerAssociation) {
    return false;
  }
  return dinerAssociation.in_trial;
}

/**
 * @param subscription The subscription to check
 * @returns whether the subscription is connected to lyft
 */
export function isLyftMember(subscription: SubscriptionData): boolean {
  if (!subscription) {
    return false;
  }
  const { managed_settings: managedSettings } = subscription;
  if (!managedSettings) {
    return false;
  }
  return (
    managedSettings.cobrand === MANAGED_SUBSCRIPTION_PARTNERS.LYFT &&
    (managedSettings.status === 'ACTIVE' || managedSettings.status === 'PAUSED')
  );
}

/**
 * @param subscription - The subscription to check
 * @returns Whether the subscription is connected to amazon prime
 */
export function isPrimeMember(subscription: SubscriptionData): boolean {
  if (!subscription) {
    return false;
  }
  const { managed_settings: managedSettings } = subscription;
  if (!managedSettings) {
    return false;
  }
  return (
    managedSettings.cobrand === MANAGED_SUBSCRIPTION_PARTNERS.AMAZON_PRIME &&
    (managedSettings.status === 'ACTIVE' || managedSettings.status === 'PAUSED')
  );
}

export function isManagedSubscription(subscription: SubscriptionData): boolean {
  if (!subscription) {
    return false;
  }
  const { managed_settings: managedSettings } = subscription;
  if (!managedSettings) {
    return false;
  }

  return true;
}
/**
 * Returns if subscription is eligible to be migrated.  Subscription can be migrated if it is in the `active` or `cancelled` state.
 * `cancelled` state is used to indicate that the user has cancelled their subscription, but it has not yet expired and can be treated as an active subscription.
 * @param subscription subscription to inspect
 */
export function isEligibleForMigration(
  subscription: SubscriptionData,
): boolean {
  if (!subscription) {
    return false;
  }
  return (
    getIsSubscriptionActive(subscription) ||
    getIsSubscriptionCanceled(subscription)
  );
}

export function getSubscriptionEventData(
  subscriptionData: SubscriptionData,
  activeSubscriptionData?: SubscriptionData,
) {
  let subscriptionId;
  let activeSubsciptionId;

  if (!subscriptionData) {
    return {
      subscription_id: '',
    };
  }

  const isAutoOptIn = subscriptionData?.managed_settings?.auto_opt_in;
  const isActive = subscriptionData?.managed_settings?.status === 'ACTIVE';
  const isActiveAutoOptInSubscription = isAutoOptIn && isActive;

  if (isActiveAutoOptInSubscription) {
    subscriptionId = subscriptionData?.managed_settings?.paid_subscription_id;
    activeSubsciptionId = subscriptionData?.subscription_id;
  } else {
    subscriptionId = subscriptionData?.subscription_id;
    activeSubsciptionId = activeSubscriptionData?.subscription_id;
  }

  const suiteId = subscriptionData?.plan_settings?.suite_id;

  return {
    subscription_id: subscriptionId,
    ...(activeSubsciptionId && {
      active_subscription_id: activeSubsciptionId,
    }),
    ...(suiteId && {
      subscription_suite_id: suiteId,
    }),
  };
}
/**
 * Replaces <c> tags with a gold styling span tag
 * @param text string with <c> tags around text to be made gold
 */
export function colorizeSubscriptionText(text: string) {
  if (!text) {
    return '';
  }
  if (typeof text !== 'string') {
    return '';
  }
  return text
    .replace('<c>', '<span className="subscription-highlight-color">')
    .replace('</c>', '</span>');
}
/**
 * Replaces text inside curly brackets with a subscriptin object values
 * @param text text with curly brace templates (e.g {diner_association.in_trial})
 * @param subscription function replaces templates with value from this subscription object
 * @param transform function that transforms value by custom rule
 */
export function replaceTemplatesInV2Text(
  text: string,
  subscription: SubscriptionData,
  transform?: (value: unknown, field: string) => string,
): string {
  if (!text && typeof text !== 'string') {
    return '';
  }

  // Extracts all subscription object keys between curly braces
  const subscriptionObjectKeys = text.match(/[^{]+(?=\})/g);

  if (!subscriptionObjectKeys) {
    return text;
  }

  // Creates a string with replaced subscriptionObjectKeys by actual values
  // If transformer exists, applies it to actual value
  return subscriptionObjectKeys.reduce((currentText, subscriptionObjKey) => {
    let value = get(subscription, subscriptionObjKey) || '';

    if (typeof transform === 'function') {
      value = transform(value, subscriptionObjKey);
    }

    return currentText.replace(`{${subscriptionObjKey}}`, value);
  }, text);
}
/**
 * Returns true/false indicating if url starts with `partner/` and has a valid second path segment
 * @example isPartnerUrl('/partner/instacart/') // true
 * @example isPartnerUrl('/partner/') // false
 * @example isPartnerUrl('/plus/instacart/') // false
 * @example isPartnerUrl('/plus/partner/instacart/') // false
 * @param slug
 */
export function isPartnerPageFromSlug(slug: string): boolean {
  if (!isString(slug)) {
    return false;
  }
  const slugElements = slug.split('/');
  return slugElements[1] === 'partner' && !!slugElements[2]; // 0th element will be empty string as path starts with `/` and we split on '/'
}
/**
 * Simple transformer for #replaceV2Text function
 * @param value any value that we want to transform
 * @param key key that we can check
 */
export function nextBillingDateTransformer(
  value: unknown,
  key: string,
): string {
  if (
    key === 'diner_association.next_billing_date' &&
    typeof value === 'string'
  ) {
    return format(value, 'MMMM D YYYY');
  }

  return String(value);
}

/**
 * Extracts possible cancellation options from subscription text data
 * @param subscription subscription to find options
 */
export function getSurveyOptions(subscription: SubscriptionData): Option[] {
  const surveyOptions =
    subscription?.texts?.cancel_upsell?.survey?.options || {};

  return Object.keys(surveyOptions).map((key) => ({
    id: key,
    value: surveyOptions[key],
  }));
}

/**
 * Finds the active payment method for a given subscription.
 *
 * @param {SubscriptionData} subscription - The subscription object containing subscription payment info.
 * @param {CreditCard[] | PaypalAccount[] | VenmoAccount[]} paymentMethodArray - An array of payment method objects to search through.
 * @returns {CreditCard | PaypalAccount | VenmoAccount | null} - The payment method object with the matching payment ID or null if no match is found.
 */
export function findActiveSubscriptionPayment<
  T extends CreditCard | PaypalAccount | VenmoAccount,
>(subscription: SubscriptionData, paymentMethodArray: T[]): T | null {
  if (!subscription.subscription_payments_info?.payment_id) {
    return null;
  }
  const encodedPaymentId = uuidToBase64(
    subscription.subscription_payments_info.payment_id,
  );
  return (
    paymentMethodArray.find(
      (paymentMethod) => paymentMethod.id === encodedPaymentId,
    ) || null
  );
}
